import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {BeatLoader} from "react-spinners";
import {documentAPI, thirdPartyProviderAPI} from "../../../Api/ifex";
import {FiX, FiUpload, FiRefreshCw, FiAlertCircle} from "react-icons/fi";

const DocumentEditModal = ({visible, document, onClose, onSuccess, user, isSuperAdmin, modules}) => {
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [fileToUpload, setFileToUpload] = useState(null);
    const [thirdPartyProviders, setThirdPartyProviders] = useState([]);

    const {register, handleSubmit, watch, setValue, formState: {errors, isValid}} = useForm({
        mode: "onChange",
        defaultValues: {
            document_name: document.document_name || '',
            is_active: document.is_active ? '1' : '0',
            module: document.module || '',
            third_party_provider_id: document.third_party_provider_id || '',
            third_party_variant: document.third_party_variant || '',
            sequence: document.sequence || '',
            remarks: document.remarks || '',
        }
    });

    useEffect(() => {
        if (visible && document.id) {
            fetchPlaceholders();
            fetchThirdPartyProviders();
        }
    }, [visible, document.id]);

    const fetchThirdPartyProviders = async () => {
        try {
            const response = await thirdPartyProviderAPI.getAll(true);
            if (response.data.success) {
                setThirdPartyProviders(response.data.providers || []);
            }
        } catch (error) {
            console.error('Error fetching third-party providers:', error);
        }
    };

    const fetchPlaceholders = async () => {
        setScanning(true);
        try {
            const response = await documentAPI.getPlaceholders(document.id);
            if (response.data.data) {
                setPlaceholders(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching placeholders:', error);
        } finally {
            setScanning(false);
        }
    };

    const onSubmit = async (data) => {
        setSaving(true);
        try {
            const payload = {
                ...data,
                is_active: data.is_active === '1',
            };

            await documentAPI.update(document.id, payload);
            toast.success('Dokument erfolgreich aktualisiert');
            onSuccess();
        } catch (error) {
            console.error('Error updating document:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Aktualisieren des Dokuments');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.endsWith('.docx')) {
                toast.error('Nur .docx Dateien sind erlaubt');
                return;
            }
            setFileToUpload(file);
        }
    };

    const handleFileUpload = async () => {
        if (!fileToUpload) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', fileToUpload);

            await documentAPI.replaceFile(document.id, formData);
            toast.success('Datei erfolgreich ersetzt');
            setFileToUpload(null);

            // Re-scan placeholders
            await fetchPlaceholders();
        } catch (error) {
            console.error('Error uploading file:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Hochladen der Datei');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleRescan = async () => {
        setScanning(true);
        try {
            const response = await documentAPI.rescan(document.id);
            if (response.data.data && response.data.data.success) {
                setPlaceholders(response.data.data.linked_placeholders || []);
                const foundCount = response.data.data.placeholders_found || 0;
                const linkedCount = response.data.data.placeholders_linked || 0;
                toast.success(`${foundCount} Platzhalter gefunden, ${linkedCount} verknüpft`);
            }
        } catch (error) {
            console.error('Error rescanning:', error);
            toast.error('Fehler beim Scannen der Platzhalter');
        } finally {
            setScanning(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                onClick={onClose}
            ></div>
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Dokument bearbeiten</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Document Name */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Dokumentname *
                                {!isSuperAdmin && <span className="text-xs text-gray-500 ml-2">(Nur Ansicht)</span>}
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.document_name ? 'border-red-500' : 'border-gray-300'} ${!isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                {...register('document_name', {
                                    required: 'Dokumentname ist erforderlich'
                                })}
                                disabled={!isSuperAdmin}
                            />
                            {errors.document_name && <p className="text-red-500 text-sm mt-1">{errors.document_name.message}</p>}
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Status *
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                {...register('is_active', {required: true})}
                            >
                                <option value="1">Aktiv</option>
                                <option value="0">Inaktiv</option>
                            </select>
                        </div>

                        {/* Module */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Modul *
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                {...register('module', {required: true})}
                            >
                                <option value="">-- Wählen --</option>
                                {modules.map((module, idx) => (
                                    <option key={idx} value={module}>{module}</option>
                                ))}
                            </select>
                        </div>

                        {/* Third-Party Provider */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Drittpartei
                                {!isSuperAdmin && <span className="text-xs text-gray-500 ml-2">(Nur Ansicht)</span>}
                            </label>
                            <select
                                className={`w-full px-3 py-2 border border-gray-300 rounded ${!isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                {...register('third_party_provider_id')}
                                disabled={!isSuperAdmin}
                            >
                                <option value="">-- Standard (Intern) --</option>
                                {thirdPartyProviders.map((provider) => (
                                    <option key={provider.id} value={provider.id}>
                                        {provider.provider_name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Optionale Drittpartei-Anbieter-Auswahl</p>
                        </div>

                        {/* Third-Party Variant */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Variante
                                {!isSuperAdmin && <span className="text-xs text-gray-500 ml-2">(Nur Ansicht)</span>}
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border border-gray-300 rounded ${!isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="z.B. Version A, Standard, Premium"
                                {...register('third_party_variant')}
                                disabled={!isSuperAdmin}
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional: Spezifische Variante des Templates</p>
                        </div>

                        {/* Sequence */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Sequenz
                                {!isSuperAdmin && <span className="text-xs text-gray-500 ml-2">(Nur Ansicht)</span>}
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border border-gray-300 rounded ${!isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="z.B. 1, 2, 3 oder A, B, C"
                                {...register('sequence')}
                                disabled={!isSuperAdmin}
                            />
                            <p className="text-xs text-gray-500 mt-1">Bestimmt die Reihenfolge beim PDF-Merge</p>
                        </div>

                        {/* Current File */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Aktuelle Datei
                            </label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded text-sm text-gray-700">
                                {document.file_name}
                            </div>
                        </div>

                        {/* Remarks */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Bemerkungen
                            </label>
                            <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                rows="3"
                                {...register('remarks')}
                            />
                        </div>
                    </div>

                    {/* File Replacement Section */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <FiUpload /> Vorlage ersetzen
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <input
                                    type="file"
                                    accept=".docx"
                                    onChange={handleFileSelect}
                                    className="w-full text-sm"
                                />
                                {fileToUpload && (
                                    <p className="text-xs text-green-600 mt-1">
                                        Ausgewählt: {fileToUpload.name}
                                    </p>
                                )}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    onClick={handleFileUpload}
                                    disabled={!fileToUpload || uploading}
                                    className={`w-full px-4 py-2 rounded text-white flex items-center justify-center gap-2 ${
                                        !fileToUpload || uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {uploading ? <BeatLoader size={8} color="#ffffff" /> : <FiUpload />}
                                    {uploading ? 'Lade hoch...' : 'Datei hochladen'}
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                            <FiAlertCircle className="inline mr-1" />
                            Nur .docx Dateien erlaubt. Nach Upload werden Platzhalter automatisch neu gescannt.
                        </p>
                    </div>

                    {/* Placeholders Section */}
                    <div className="mt-6 p-4 bg-gray-50 rounded">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold text-gray-700">
                                Erkannte Platzhalter ({placeholders.length})
                            </h3>
                            <button
                                type="button"
                                onClick={handleRescan}
                                disabled={scanning}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                                <FiRefreshCw className={scanning ? 'animate-spin' : ''} />
                                {scanning ? 'Scanne...' : 'Neu scannen'}
                            </button>
                        </div>

                        {scanning ? (
                            <div className="text-center py-4">
                                <BeatLoader size={8} color="#1e3a8a" />
                            </div>
                        ) : placeholders.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                                {placeholders.map((ph, idx) => (
                                    <div
                                        key={idx}
                                        className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700"
                                    >
                                        {ph.name || ph.placeholder_name || ph}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 text-center py-4">
                                Keine Platzhalter gefunden
                            </p>
                        )}
                    </div>

                    {/* Metadata Display */}
                    <div className="mt-6 p-4 bg-gray-50 rounded">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Metadata</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <div><strong>ID:</strong> {document.id}</div>
                            <div><strong>Upload am:</strong> {document.created_at ? new Date(document.created_at).toLocaleString('de-DE') : '-'}</div>
                            <div><strong>Upload von:</strong> {document.uploaded_by || '-'}</div>
                            <div><strong>Letzter Zugriff:</strong> {document.last_accessed_at ? new Date(document.last_accessed_at).toLocaleString('de-DE') : '-'}</div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !isValid}
                            className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
                                saving || !isValid ? 'bg-gray-400 cursor-not-allowed' : 'bg-mainBlue hover:bg-blue-700'
                            }`}
                        >
                            {saving && <BeatLoader size={8} color="#ffffff" />}
                            {saving ? 'Speichere...' : 'Speichern'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DocumentEditModal;
